import type { Asset } from '../types/ugla-types';
import type { GroupedAssets, ProductLayer, ProductRef } from '../types/vap-types';
import { getProductLayer, getProductRef, getProductView, getTag, hasRequiredTags } from './tag-helpers';

/**
 * Filters assets to only include 2D assets with required tags (product_layer and product_ref).
 * @param assets - Array of assets to filter
 * @returns Array of 2D assets that have both product_layer and product_ref tags
 */
export const getControllableAssets = (assets: Asset[]): Asset[] => {
  return assets.filter(
    (asset) => asset.type === '2D' && hasRequiredTags(asset)
  );
};

/**
 * Filters assets to only include 2D assets with required tags (product_layer and view).
 * @param assets - Array of assets to filter
 * @returns Array of 2D assets that have both product_layer and view tags
 */
export const getBackgroundAssets = (assets: Asset[]): Asset[] => {
  return assets.filter(
    (asset) => asset.type === '2D' && getProductView(asset) && getProductLayer(asset) && !getProductRef(asset)
  );
};

/**
 * Groups assets by their view, product_layer and product_ref tags.
 * @param assets - Array of assets to group
 * @returns GroupedAssets object organized by view, product_layer and product_ref
 */
export const groupAssetsByViewAndLayerAndRef = (assets: Asset[]): GroupedAssets => {
  const grouped: GroupedAssets = {};

  const controllableAssets = getControllableAssets(assets);

  controllableAssets.forEach((asset) => {
    const productView = getProductView(asset);
    const productLayer = getProductLayer(asset);
    const productRef = getProductRef(asset);

    if (productLayer && productRef && productView) {
      if (!grouped[productView]) {
        grouped[productView] = {};
      }
      if (!grouped[productView][productLayer]) {
        grouped[productView][productLayer] = {};
      }
      if (!grouped[productView][productLayer][productRef]) {
        grouped[productView][productLayer][productRef] = [];
      }
      grouped[productView][productLayer][productRef].push(asset);
    }
  });

  return grouped;
};

/**
 * Gets all unique product_layer values from an array of assets.
 * @param assets - Array of assets to extract product layers from
 * @param view - The products' view to filter by
 * @returns Array of unique product_layer values (categories)
 */
export const getAvailableProductLayersByView = (
  assets: Asset[], 
  view: string,
): string[] => {
  const controllableAssets = getControllableAssets(assets);
  const layers = new Set<string>();

  controllableAssets
  .filter((asset) => {
    const productView = getProductView(asset);
    return view === productView;
  })
  .forEach((asset) => {
    const productLayer = getProductLayer(asset);
    if (productLayer) {
      layers.add(productLayer);
    }
  });

  return Array.from(layers).sort();
};

/**
 * Gets all unique view values from an array of assets.
 * @param assets - Array of assets to extract product views from
 * @returns Array of unique view values
 */
export const getAvailableProductViews = (assets: Asset[]): string[] => {
  const controllableAssets = getControllableAssets(assets);
  const views = new Set<string>();

  controllableAssets.forEach((asset) => {
    const productView = getProductView(asset);
    if (productView) {
      views.add(productView);
    }
  });

  return Array.from(views).sort();
};

/**
 * Gets asset url for 360 view.
 * @param assets - Array of assets to extract 360 from
 * @returns String representing the url of 360 view
 */
export const getAvailable360View = (assets: Asset[]): string => {
  const assets360 = assets.filter((asset) => 
    asset.type === "2D" && getTag("view", asset.tags) === "360" && getTag("product_type", asset.tags) === "360"
  );

  return assets360.length > 0 ? assets360[0].url : "";
};

/**
 * Gets all unique product_ref values for a specific product_layer.
 * @param assets - Array of assets to search
 * @param productLayer - The product_layer to filter by
 * @returns Array of unique product_ref values for the given layer
 */
export const getAvailableProductRefsForLayer = (
  assets: Asset[],
  productLayer: string,
): string[] => {
  const controllableAssets = getControllableAssets(assets);
  const refs = new Set<string>();

  controllableAssets.forEach((asset) => {
    const layer = getProductLayer(asset);
    const ref = getProductRef(asset);
    const view = getProductView(asset);
    if (layer === productLayer && ref && view) {
      refs.add(ref);
    }
  });

  return Array.from(refs).sort();
};

/**
 * Gets all unique product_ref values for a specific product_layer.
 * @param assets - Array of assets to search
 * @param productView - The product's view to filter by
 * @param productLayer - The product_layer to filter by
 * @returns Array of unique product_ref values for the given layer
 */
export const getAvailableProductRefsForViewAndLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string,
): string[] => {
  const controllableAssets = getControllableAssets(assets);
  const refs = new Set<string>();

  controllableAssets.forEach((asset) => {
    const view = getProductView(asset);
    const layer = getProductLayer(asset);
    const ref = getProductRef(asset);
    if (layer === productLayer && ref && view === productView) {
      refs.add(ref);
    }
  });

  return Array.from(refs).sort();
};

/**
 * Gets all assets that match a specific product_layer and product_ref.
 * @param assets - Array of assets to search
 * @param productLayer - The product_layer to filter by
 * @param productRef - The product_ref to filter by
 * @returns Array of assets matching both criteria
 */
export const getAssetsByLayerAndRef = (
  assets: Asset[],
  productLayer: string,
  productRef: string
): Asset[] => {
  return getControllableAssets(assets).filter((asset) => {
    const layer = getProductLayer(asset);
    const ref = getProductRef(asset);
    return layer === productLayer && ref === productRef;
  });
};

/**
 * Gets all assets that match a specific product_layer and product_ref.
 * @param assets - Array of assets to search
 * @param productView - The product's view to filter by
 * @param productLayer - The product_layer to filter by
 * @param productRef - The product_ref to filter by
 * @returns Array of assets matching both criteria
 */
export const getAssetsByViewAndLayerAndRef = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  productRef: string,
): Asset[] => {
  return getControllableAssets(assets).filter((asset) => {
    const view = getProductView(asset);
    const layer = getProductLayer(asset);
    const ref = getProductRef(asset);
    return layer === productLayer && ref === productRef && view === productView;
  });
};

/**
 * Gets all assets for a specific product_layer, regardless of product_ref.
 * @param assets - Array of assets to search
 * @param productLayer - The product_layer to filter by
 * @returns Array of assets matching the product_layer
 */
export const getAssetsByLayer = (assets: Asset[], productLayer: string): Asset[] => {
  return getControllableAssets(assets).filter((asset) => {
    const layer = getProductLayer(asset);
    return layer === productLayer;
  });
};

/**
 * Gets all assets for a specific view.
 * @param assets - Array of assets to search
 * @param productLayer - The view to filter by
 * @returns Array of assets matching the view
 */
export const getAssetsByView = (assets: Asset[], productView: string): Asset[] => {
  return getControllableAssets(assets).filter((asset) => {
    const view = getProductView(asset);
    return view === productView;
  });
};

/**
 * Gets all assets for a specific product_layer, regardless of product_ref.
 * @param assets - Array of assets to search
 * @param productView - The product's view to filter by
 * @param productLayer - The product_layer to filter by
 * @returns Array of assets matching the product_layer
 */
export const getAssetsByLayerAndView = (assets: Asset[], productView: string, productLayer: string): Asset[] => {
  return getControllableAssets(assets).filter((asset) => {
    const view = getProductView(asset);
    const layer = getProductLayer(asset);
    return layer === productLayer && view === productView;
  });
};

/**
 * Converts grouped assets into an array of ProductLayer objects.
 * @param groupedAssets - GroupedAssets object
 * @returns Array of ProductLayer objects
 */
export const groupedAssetsToProductLayers = (
  groupedAssets: GroupedAssets
): ProductLayer[] => {
  return Object.keys(groupedAssets).reduce((acc : ProductLayer[], view : string) => {
    return [...acc, ...Object.entries(groupedAssets[view]).map(([layerType, refsMap]) => {
      // refsMap is of type { [productRef: string]: Asset[] }
      const refs = refsMap as { [productRef: string]: Asset[] };
      const assets: Asset[] = Object.values(refs).flat();
      return {
        layerType,
        view,
        assets,
      };
    })];
  }, []);
};

/**
 * Converts grouped assets for a specific layer into an array of ProductRef objects.
 * @param groupedAssets - GroupedAssets object
 * @param productView - The product's associated view
 * @param productLayer - The product_layer to extract refs for
 * @returns Array of ProductRef objects
 */
export const groupedAssetsToProductRefs = (
  groupedAssets: GroupedAssets,
  productView: string,
  productLayer: string,
): ProductRef[] => {
  const refsMap = groupedAssets[productView][productLayer];
  if (!refsMap) {
    return [];
  }

  return Object.entries(refsMap).map(([ref, assets]) => {
    // Use the first asset as the representative asset for this ref
    // In most cases, there should be one asset per ref, but we handle multiple
    // assets is of type Asset[] from the GroupedAssets type
    // Object.entries returns [string, unknown], so we need to assert the type
    const assetArray = assets as Asset[];
    if (assetArray.length === 0) {
      throw new Error(`No assets found for product_ref: ${ref}`);
    }
    return {
      ref,
      asset: assetArray[0],
    };
  });
};

/**
 * Gets the first asset for a specific product_layer and product_ref combination.
 * Useful when you need a single representative asset for a layer/ref combination.
 * @param assets - Array of assets to search
 * @param productLayer - The product_layer to filter by
 * @param productRef - The product_ref to filter by
 * @returns The first matching asset or undefined
 */
export const getFirstAssetByLayerAndRef = (
  assets: Asset[],
  productLayer: string,
  productRef: string
): Asset | undefined => {
  const matchingAssets = getAssetsByLayerAndRef(assets, productLayer, productRef);
  return matchingAssets.length > 0 ? matchingAssets[0] : undefined;
};

/**
 * Gets the first asset for a specific product_layer and product_ref combination.
 * Useful when you need a single representative asset for a layer/ref combination.
 * @param assets - Array of assets to search
 * @param productView - The view to filter by
 * @param productLayer - The product_layer to filter by
 * @param productRef - The product_ref to filter by
 * @returns The first matching asset or undefined
 */
export const getFirstAssetByViewAndLayerAndRef = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  productRef: string,
): Asset | undefined => {
  const matchingAssets = getAssetsByViewAndLayerAndRef(assets, productView, productLayer, productRef);
  return matchingAssets.length > 0 ? matchingAssets[0] : undefined;
};

/**
 * Gets the first asset for a specific view.
 * Useful when you need a single representative asset for a view.
 * @param assets - Array of assets to search
 * @param productView - The view to filter by
 * @returns The first matching asset or undefined
 */
export const getFirstAssetByView = (
  assets: Asset[],
  productView: string,
): Asset | undefined => {
  const matchingAssets = getAssetsByView(assets, productView);
  return matchingAssets.length > 0 ? matchingAssets[0] : undefined;
};